import { useState } from "react";
import { ledgerPaid } from "src/print-configuration/PrintConfigration";
import { closeLoading, notifyError, notifyLoading, notifySuccess } from "src/assets/toastService";
import { cardPayment, postLedger, verifyPayment } from "src/shared/services/servicesv2";
import { LedgerDetails } from "src/shared/services/interfacev2";
import { initRazorpayCheckout } from "src/paymentGatway/paymentCheck";
import { useUser } from "src/business-types/grocery/components/UserContext";

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface RazorpayOrderResponse {
  key: string;
  amount: number;
  currency: string;
  order_id: string;
}

interface RazorpayHandlerResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export const usePayment = (
  customerData: LedgerDetails,
  customerId: string,
  onClose: () => void,
  refetchLedger: () => void
) => {
  const [paymentLoading, setPaymentLoading] = useState<boolean>(false);
  const [print, setPrintData] = useState<ledgerPaid>();
  // useUser() may not have exact typings for userConfiguration; cast to any to avoid TS error
  const { userConfiguration } = useUser() as any;
  // Saves the ledger entry and builds the receipt/print payload.
  // Shared by both card and non-card payment flows.
  const finalizePayment = async (paymentDetails: Record<string, any>, method: string) => {
    const res = await postLedger(paymentDetails);
    const currentBalance = Number(customerData.totalOutstandingBalance ?? 0);
    const isAdvance = currentBalance < 0;
    const paidAmount = Number(paymentDetails.payAmount ?? 0);
    const discount = Number(paymentDetails.discount ?? 0);


    let formattedClientId = "0000";
    if (customerData?.customerDetails?.refId) {
      formattedClientId = String(customerData.customerDetails.refId).padStart(4, "0");
    }

    const customerName = [
      customerData?.customerDetails?.firstName,
      customerData?.customerDetails?.middleName,
      customerData?.customerDetails?.lastName,
    ]
      .filter((name) => name && name !== "undefined" && name !== "null")
      .join(" ");

    const printData: ledgerPaid = {
      Balance: Math.abs(Number(currentBalance.toFixed(2))),
      Paid: isAdvance ? -Math.abs(paidAmount) : Math.abs(paidAmount),
      PreviousBalance: Math.abs(Number(currentBalance.toFixed(2))),
      previousLabel: isAdvance ? "Prev. Advance(السابق. يتقدم)" : "Prev. Balance(السابق. توازن)",
      paidLabel: isAdvance ? "Refund Amount(مبلغ الاسترداد)" : "Balance Paid(الرصيد المدفوع)",
      balanceLabel: isAdvance ? "Remaining Advance(التقدم المتبقي)" : "New Balance(توازن جديد)",
      isAdvance,
      billNo: res?.data?.billNumber || "",
      clientId: formattedClientId,
      CustomerPhon: Number(customerData.customerDetails?.phones?.[0]?.phone || 0),
      CustomerName: customerName,
      Time: paymentDetails.paidTime,
      Date: paymentDetails.paidDate,
      paymentMethod: method || "",
      discount,
    };

    setPrintData(printData);
    notifySuccess(isAdvance ? "Advance adjusted successfully" : "Payment received successfully");

    setTimeout(() => {
      refetchLedger();
      onClose();
    }, 2000);
  };

  const handlePayment = async (amount: string, method: string, discounts: string) => {
    if (!amount || parseFloat(amount) <= 0) {
      notifyError("Enter a valid amount");
      return;
    }

    setPaymentLoading(true);

    try {
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, "0");
      const day = String(currentDate.getDate()).padStart(2, "0");
      const hours = String(currentDate.getHours()).padStart(2, "0");
      const minutes = String(currentDate.getMinutes()).padStart(2, "0");
      const seconds = String(currentDate.getSeconds()).padStart(2, "0");

      const formattedDate = `${year}-${month}-${day}`;
      const formattedTime = `${hours}:${minutes}:${seconds}`;
      const paidAmount = parseFloat(amount);
      const discount = parseFloat(Number(discounts).toFixed(2)) || 0;
      const currentBalance = Number(customerData.totalOutstandingBalance ?? 0);
      const normalizedPayAmount = currentBalance < 0 ? -Math.abs(paidAmount) : Math.abs(paidAmount);

      const paymentDetails = {
        customerId,
        outstandingBalance: currentBalance,
        orderamount: "0",
        payAmount: normalizedPayAmount,
        discount,
        paymentMethod: method,
        paidDate: formattedDate,
        paidTime: formattedTime,
        orderDate: formattedDate,
        orderTime: formattedTime,
      };

      // ---------- CARD PAYMENT FLOW (case-insensitive) ----------
      if (String(method).toLowerCase() === "card" && userConfiguration?.cardPayment === true) {
        notifyLoading("Loading payment...");

        const loaded = await initRazorpayCheckout();
        if (!loaded) {
          notifyError("Razorpay failed to load");
          setPaymentLoading(false);
          return;
        }

        // cardPayment() returns the unwrapped payload (see service fix below)
        const paymentData: RazorpayOrderResponse = await cardPayment({
          amount: Math.round(paidAmount * 100),
        });

        const options = {
          key: paymentData.key,
          amount: paymentData.amount,
          currency: paymentData.currency,
          order_id: paymentData.order_id,
          name: "POS System",
          description: `Ledger payment for ${customerId}`,

          handler: async (response: RazorpayHandlerResponse) => {
            try {
              // verifyPayment() should also return unwrapped data (see note below)
              const verifyData = await verifyPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });

              if (!verifyData.valid) {
                notifyError("Payment verification failed");
                setPaymentLoading(false);
                return;
              }

              const payment = verifyData.payment;

              await finalizePayment(
                {
                  ...paymentDetails,
                  paymentId: payment.id,
                  razorpayOrderId: payment.order_id,
                  razorpaySignature: response.razorpay_signature,
                  status: payment.status,
                  gatewayMethod: payment.method,
                  bank: payment.bank,
                  wallet: payment.wallet,
                  vpa: payment.vpa,
                  email: payment.email,
                  contact: payment.contact,
                  fee: payment.fee,
                  tax: payment.tax,
                  currency: payment.currency,
                  gatewayAmount: payment.amount,
                },
                "Card"
              );
            } catch (err) {
              console.error("Verification error:", err);
              notifyError("Payment verification failed");
            } finally {
              closeLoading();
              setPaymentLoading(false);
            }
          },

          modal: {
            ondismiss: () => {
              notifyError("Payment Cancelled");
              closeLoading();
              setPaymentLoading(false);
            },
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
        return; // loading state is cleared inside handler/ondismiss, not here
      }

      // ---------- NON-CARD (CASH / OTHER) FLOW ----------
      await finalizePayment(paymentDetails, method);
    } catch (error) {
      console.error("Payment error:", error);
      notifyError("Payment failed. Try again.");
    } finally {
      closeLoading();
      if (method !== "card") {
        setPaymentLoading(false);
      }
    }
  };

  return {
    handlePayment,
    paymentLoading,
    print,
  };
};